<script setup lang="ts">
import BalmHeader from '@/vue/organisms/header/balm-header.vue';
import BalmSidebar from '@/vue/organisms/sidebar/balm-sidebar.vue';
import AllianzBrandingFooter from '@/vue/molecules/allianz-branding-footer.vue';
import {useUserStore} from '@/ts/store/user-store';
import {useDrawerStore} from '@/ts/store/drawer-store';
import type {NavigationItem} from '@/ts/types/component/navigation';
import {SHOW_SCREEN_SIZE} from '@/ts/utils/consts';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import {onBeforeRouteUpdate, useRouter} from 'vue-router';
import {computed, nextTick, onBeforeUnmount, ref} from 'vue';
import {useOopsieStore} from '@/ts/store/oopsie-store';
import {storeToRefs} from 'pinia';
import type {BalmSidebarMenuItem} from '@/ts/types/component/balm-sidebar';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {useAllianzBranding} from '@/ts/composables/stateful/use-allianz-branding';
import {faqIntroVideo} from '@/ts/composables/pure/use-faq-sidebar';
import {commitNavigation, isPopNavigation} from '@/ts/utils/history-navigation';

const userStore = useUserStore();
const {canAccessSidebar} = useFeatureAccess();
const drawerStore = useDrawerStore();
const {balmSidebarState} = storeToRefs(drawerStore);

function onContentClick(): void {
  if (balmSidebarState.value) {
    drawerStore.closeBalmSidebarDrawer();
  }
}

const contentRef = ref<HTMLElement | null>(null);

const props = defineProps<{
  enableGuideDrawer?: boolean;
  mainNavigationItems?: NavigationItem[];
  sidebarMenuItems: BalmSidebarMenuItem[];
}>();

defineEmits<{
  clickMainNavigationItem: [string],
}>();

const router = useRouter();

const isScrollingDisabled = computed((): boolean => {
  return router.currentRoute.value.meta.disablePageScrollingInTemplate === true;
});

const {screenSize} = useScreenSize();

const oopsieStore = useOopsieStore();
const {oopsPageTemplateVisible} = storeToRefs(oopsieStore);

const enableGuideDrawer = computed(() => {
  return props.enableGuideDrawer && !oopsPageTemplateVisible.value && canAccessSidebar.value;
});

const {isAllianzUser} = useAllianzBranding();

const introVideo = computed(() => faqIntroVideo(userStore.homeContent));

// The page scrolls inside `.content` (not window), so the router's scrollBehavior can't
// restore it — save per route and restore on back/forward.
const SAVED_SCROLL_LIMIT = 20;
const savedScroll = new Map<string, number>();

function rememberScroll(fullPath: string, top: number): void {
  savedScroll.delete(fullPath);
  savedScroll.set(fullPath, top);
  // Bounded: routes never popped back to would otherwise accumulate for the layout's life.
  for (const stalest of savedScroll.keys()) {
    if (savedScroll.size <= SAVED_SCROLL_LIMIT) {
      break;
    }
    savedScroll.delete(stalest);
  }
}

onBeforeRouteUpdate((to, from) => {
  if (!contentRef.value) {
    return;
  }
  rememberScroll(from.fullPath, contentRef.value.scrollTop);
  if (!isPopNavigation()) {
    // Pushes reset synchronously, as before — no frame of the new page at the old offset.
    contentRef.value.scrollTop = 0;
  }
});

// Restoring a pop has to wait for the (kept-alive) page DOM to be back, or the write
// clamps to 0 against the outgoing page's height.
const removeAfterEach = router.afterEach((to, _from, failure) => {
  // Duplicated/failed navigations (e.g. clicking the already-active nav item) change nothing.
  if (failure) {
    return;
  }
  const isPop = isPopNavigation();
  // Kept-alive pages read this in onActivated, which runs after this hook.
  commitNavigation();
  if (!isPop) {
    return;
  }
  const target = savedScroll.get(to.fullPath) ?? 0;
  savedScroll.delete(to.fullPath);
  void nextTick(() => {
    if (contentRef.value) {
      contentRef.value.scrollTop = target;
    }
  });
});

onBeforeUnmount(removeAfterEach);

</script>
<template>
  <div class="global-page-template">
    <balm-header
      :main-navigation-items="mainNavigationItems"
      :sidebar-menu-items="sidebarMenuItems"
      @clickMainNavigationItem="$emit('clickMainNavigationItem', $event)"
    />
    <main class="main">
      <div ref="contentRef" class="content" :class="{'content--no-scroll': isScrollingDisabled}" @click="onContentClick">
        <slot/>
        <allianz-branding-footer v-if="isAllianzUser && !isScrollingDisabled"/>
      </div>
      <balm-sidebar
        v-if="enableGuideDrawer && userStore.homeContent"
        :sidebar-menu-items="sidebarMenuItems"
        :faq="userStore.homeContent.faq"
        :resources="userStore.homeContent.resources"
        :glossaries="userStore.homeContent.glossaries"
        :imprint="userStore.homeContent.imprint"
        :intro-video="introVideo"
      />
    </main>
    <div v-if="SHOW_SCREEN_SIZE" class="screen-size">
      {{ screenSize }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.global-page-template {
  display: flex;
  width: 100%;
  flex-direction: column;
}

.main {
  height: calc(100% - 50px); // mobile header 50px
  display: flex;
  flex-direction: row;

  @include screen-lg {
    height: calc(100% - 80px); // desktop header 80px
  }
}

.content {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;

  &:not(.content--no-scroll) {
    overflow: auto;
  }

  &.content--no-scroll {
    height: 100%;
    min-height: 0;
    overflow: auto;

    @include screen-lg {
      overflow: visible;
      height: auto;
      min-height: 100%;
    }
  }
}

.screen-size {
  position: fixed;
  bottom: 0;
  left: 0;
  background-color: var(--Neutral-UI-neutral-ui-60);
  font-size: 10px;
  padding: 0 5px;
  z-index: 2;
  line-height: 16px;
  color: white;
  border-top-right-radius: 6px;
}

</style>
